import type { TimeEntry, WorkTypePart } from '@/data/scheduleTimeTracking';
export type WorkPartDraft = { id: string; workType: string; minutes: string; originalMinutes: string; originalSeconds: string };
export function secondsMicros(value: string): bigint {
  if (!/^\d+(?:\.\d{1,6})?$/.test(value)) throw new Error('Enter a positive duration with at most six decimal seconds.');
  const [whole, decimal = ''] = value.split('.');
  return BigInt(whole) * 1_000_000n + BigInt(decimal.padEnd(6, '0'));
}
export function secondsText(value: bigint): string { return `${value / 1_000_000n}.${String(value % 1_000_000n).padStart(6, '0')}`; }
function timestampMicros(value: string): bigint {
  const fractional = value.match(/\.(\d{1,6})(?:Z|[+-]\d{2}:\d{2})$/)?.[1] ?? '';
  const whole = value.replace(/\.\d+(?=Z|[+-]\d{2}:\d{2}$)/, '');
  const millis = Date.parse(whole);
  if (!Number.isFinite(millis)) throw new Error('Enter valid clock times.');
  return BigInt(millis) * 1000n + BigInt(fractional.padEnd(6, '0'));
}
export function paidSecondsForTimes(start: string, end: string | null, lunch: number | null): string | null {
  if (!end || lunch == null) return null;
  const paid = timestampMicros(end) - timestampMicros(start) - BigInt(lunch) * 60_000_000n;
  if (paid < 0n) throw new Error('Lunch cannot exceed the entry duration.');
  return secondsText(paid);
}
export function draftPart(workType: string, seconds: string): WorkPartDraft {
  const minutes = String(Number(seconds) / 60);
  return { id: crypto.randomUUID(), workType, minutes, originalMinutes: minutes, originalSeconds: seconds };
}
export function entryWorkParts(entry: TimeEntry): WorkPartDraft[] {
  const parts = entry.work_type_parts;
  if (parts?.length) return parts.map(part => draftPart(part.work_type ?? '', part.end_seconds ? secondsText(secondsMicros(part.end_seconds) - secondsMicros(part.start_seconds)) : '0.000000'));
  return [draftPart(entry.installation_labor_purpose ?? (entry.needs_allocation ? '' : entry.allocation_label ?? ''), entry.paid_seconds ?? '0.000000')];
}
function draftMicros(row: WorkPartDraft): bigint {
  if (row.minutes === row.originalMinutes) return secondsMicros(row.originalSeconds);
  if (!/^\d+(?:\.\d{1,6})?$/.test(row.minutes)) throw new Error('Enter positive minutes with at most six decimal places.');
  const [whole, decimal = ''] = row.minutes.split('.');
  return (BigInt(whole) * 1_000_000n + BigInt(decimal.padEnd(6, '0'))) * 60n;
}
export function workPartsPayload(rows: WorkPartDraft[], paidSeconds: string | null): WorkTypePart[] | null {
  if (rows.length === 1 && !rows[0].workType) return null; // Preserve older unknown classification on a time-only correction.
  if (rows.some(row => !row.workType)) throw new Error('Choose a work type for every part. General is available for general work.');
  if (paidSeconds == null) {
    if (rows.length !== 1) throw new Error('Choose one work type while the clock is running.');
    return [{ work_type: rows[0].workType, start_seconds: '0.000000', end_seconds: null }];
  }
  const total = secondsMicros(paidSeconds);
  if (total === 0n && rows.length === 1) return [{ work_type: rows[0].workType, start_seconds: '0.000000', end_seconds: '0.000000' }];
  let used = 0n;
  return rows.map((row, index) => {
    const duration = index === rows.length - 1 ? total - used : draftMicros(row);
    if (duration <= 0n || used + duration > total) throw new Error('Each part must be positive and all parts must fit the paid duration, excluding lunch.');
    const start = used; used += duration;
    return { work_type: row.workType, start_seconds: secondsText(start), end_seconds: secondsText(used) };
  });
}
export function splitLastPart(rows: WorkPartDraft[], paidSeconds: string): WorkPartDraft[] {
  const payload = workPartsPayload(rows, paidSeconds);
  const total = secondsMicros(paidSeconds);
  const start = payload?.[payload.length - 1]?.start_seconds ?? '0.000000';
  const remaining = total - secondsMicros(start);
  if (remaining < 2n) throw new Error('There is not enough paid time to split.');
  const last = rows[rows.length - 1];
  return [...rows.slice(0, -1), draftPart(last.workType, secondsText(remaining / 2n)), draftPart(last.workType, secondsText(remaining - remaining / 2n))];
}
